/**
 * PATH: packages/ui/src/components/DataPagination.tsx
 * FILE_ID: UI-COMPONENTS-DATAPAGINATION-001
 * PURPOSE: Adaptive offset+total table footer — count-first; pager only when needed.
 * WHY: Hubs forked near-identical pagers; always-on chrome on one-page lists is noise.
 * LANDMINE: clearance="hub-chrome" keeps left-cluster controls clear of floating
 *   Feedback/Teams/AI (operator 2026-07-14). Do not put primary controls on the right.
 * RELATED: packages/job-hub JobsHubPagination (migrate consumers onto this)
 */
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '../lib/utils'

export const DATA_PAGINATION_DEFAULT_PAGE_SIZES = [50, 100, 200] as const

export type DataPaginationClearance = 'none' | 'hub-chrome'

export type DataPaginationProps = {
  page: number
  pageCount: number
  pageSize: number
  total: number
  rangeStart: number
  rangeEnd: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
  pageSizes?: readonly number[]
  /** Singular/plural base noun, e.g. "job" → "36 jobs" / "1 job". Default "result". */
  noun?: string
  clearance?: DataPaginationClearance
  className?: string
}

const controlBtn = cn(
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border',
  'bg-background text-foreground shadow-xs transition-colors',
  'hover:bg-accent hover:text-accent-foreground',
  'disabled:cursor-not-allowed disabled:opacity-40',
  'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
)

const selectClass = cn(
  'h-8 rounded-md border border-border bg-background px-2 text-sm text-foreground shadow-xs',
  'focus:border-ring focus:outline-none focus:ring-[3px] focus:ring-ring/50',
)

function pluralize(count: number, noun: string): string {
  if (count === 1) return `${count} ${noun}`
  if (noun.endsWith('s')) return `${count} ${noun}`
  return `${count} ${noun}s`
}

function countLabel(total: number, noun: string): string {
  return pluralize(total, noun)
}

function rangeLabel(
  total: number,
  rangeStart: number,
  rangeEnd: number,
  noun: string,
): string {
  const unit = total === 1 ? noun : noun.endsWith('s') ? noun : `${noun}s`
  return `${rangeStart}\u2013${rangeEnd} of ${total} ${unit}`
}

export function DataPagination({
  page,
  pageCount,
  pageSize,
  total,
  rangeStart,
  rangeEnd,
  onPageChange,
  onPageSizeChange,
  pageSizes = DATA_PAGINATION_DEFAULT_PAGE_SIZES,
  noun = 'result',
  clearance = 'none',
  className,
}: DataPaginationProps) {
  if (total === 0) return null

  const hubChrome = clearance === 'hub-chrome'
  const showPager = pageCount > 1
  const showDensity = showPager && typeof onPageSizeChange === 'function'

  if (!showPager) {
    return (
      <div
        data-data-pagination=""
        data-pagination-state="count-only"
        className={cn(
          'mt-3 flex min-w-0 items-center text-sm text-foreground',
          hubChrome ? 'mb-16 pb-4 pr-4' : 'mb-2 pb-2',
          className,
        )}
      >
        <p className="tabular-nums" aria-live="polite">
          {countLabel(total, noun)}
        </p>
      </div>
    )
  }

  return (
    <div
      data-data-pagination=""
      data-pagination-state="paged"
      className={cn(
        'mt-3 flex min-w-0 flex-wrap items-center gap-2 border-t border-border pt-3',
        'text-sm text-muted-foreground',
        hubChrome ? 'mb-16 pb-4 pr-4' : 'mb-2 pb-2',
        className,
      )}
    >
      <p className="tabular-nums text-foreground" aria-live="polite">
        {rangeLabel(total, rangeStart, rangeEnd, noun)}
      </p>

      <span className="text-muted-foreground/50" aria-hidden="true">
        ·
      </span>

      <div className="inline-flex items-center gap-1" role="group" aria-label="Page">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className={controlBtn}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <span className="min-w-[7.5rem] px-1 text-center tabular-nums text-foreground">
          Page {page} of {pageCount}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          className={controlBtn}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {showDensity ? (
        <>
          <span className="text-muted-foreground/50" aria-hidden="true">
            ·
          </span>
          <label className="inline-flex items-center gap-2">
            <span className="sr-only">Rows per page</span>
            <select
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              aria-label="Rows per page"
              className={selectClass}
            >
              {pageSizes.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </label>
        </>
      ) : null}
    </div>
  )
}
